import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { organization, user } from '../auth'
import { SchoolRemovalRequestStatusEnum } from './enums'

/**
 * One parent's request that a school release their child, so that the parent can then delete the
 * child's account.
 *
 * The mirror image of {@link HomeConnectRequest}: there, a school asks a family to take a child in;
 * here, a family asks a school to let one go. Deliberately a nudge and not a negotiation — the school
 * has no accept or reject, it simply uses "Keluarkan dari sekolah" on its own roster, and that
 * removal marks this row `RESOLVED`. Nothing in the product acts on this row automatically: the
 * parent re-confirms the deletion afterwards, so a request made days ago can never delete an account
 * on its own.
 *
 * This table exists rather than a `dedupeKey` on `notification_event` because both sides need to
 * read the state: the parent's dialog renders "already sent, N resends left", and the school's
 * removal has to find the row to resolve it.
 */
export const SchoolRemovalRequest = pgTable(
  'school_removal_request',
  {
    id: uuid().primaryKey().defaultRandom(),
    childUserId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    schoolOrganizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    /** The parent. Always an adult who owns the child's family organization. */
    requestedBy: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    /**
     * Free text the parent typed, quoted verbatim in the school's notification and email. Nullable:
     * the textarea is prefilled but clearable, and the request reads correctly without it.
     */
    message: text(),
    status: SchoolRemovalRequestStatusEnum().notNull().default('PENDING'),
    resendCount: integer().notNull().default(0),
    requestedAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    /** Set when the school actually removed the student. */
    resolvedAt: timestamp({ mode: 'date', withTimezone: true }),
    cancelledAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    /**
     * One live request per child per school, enforced by the database rather than by the router
     * alone — two tabs, or a double-click, would otherwise both pass the router's read and insert.
     *
     * Scoped to the pair rather than to the child alone, unlike the connect table's index: a removal
     * request is addressed to one named school and competes with nothing, so a child sitting in two
     * schools may legitimately have one outstanding request against each.
     */
    uniqueIndex('idx_school_removal_request_one_pending_per_child_school')
      .on(t.childUserId, t.schoolOrganizationId)
      .where(sql`${t.status} = 'PENDING'`),
    // The school's own list of outstanding requests.
    index('idx_school_removal_request_school_status').on(t.schoolOrganizationId, t.status),
    // The expiry sweep.
    index('idx_school_removal_request_status_expires_at').on(t.status, t.expiresAt),
  ],
)

export const schoolRemovalRequestRelations = relations(SchoolRemovalRequest, ({ one }) => ({
  child: one(user, {
    fields: [SchoolRemovalRequest.childUserId],
    references: [user.id],
    relationName: 'schoolRemovalRequestChild',
  }),

  school: one(organization, {
    fields: [SchoolRemovalRequest.schoolOrganizationId],
    references: [organization.id],
    relationName: 'schoolRemovalRequestSchool',
  }),

  requester: one(user, {
    fields: [SchoolRemovalRequest.requestedBy],
    references: [user.id],
    relationName: 'schoolRemovalRequestRequester',
  }),
}))

export const InsertSchoolRemovalRequestSchema = createInsertSchema(SchoolRemovalRequest, {
  childUserId: z.string().min(1),
  schoolOrganizationId: z.string().min(1),
  requestedBy: z.string().min(1),
  message: z.string().nullable().optional(),
  expiresAt: z.date(),
}).omit({
  id: true,
  status: true,
  resendCount: true,
  resolvedAt: true,
  cancelledAt: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateSchoolRemovalRequestSchema = createUpdateSchema(SchoolRemovalRequest, {
  status: z.enum(SchoolRemovalRequestStatusEnum.enumValues),
  resendCount: z.number().int().nonnegative(),
  expiresAt: z.date(),
  resolvedAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
})
  .omit({
    id: true,
    childUserId: true,
    schoolOrganizationId: true,
    requestedBy: true,
    message: true,
    requestedAt: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectSchoolRemovalRequestSchema = createSelectSchema(SchoolRemovalRequest, {
  id: z.uuid(),
  childUserId: z.string(),
  schoolOrganizationId: z.string(),
  requestedBy: z.string(),
  message: z.string().nullable(),
  status: z.enum(SchoolRemovalRequestStatusEnum.enumValues),
  resendCount: z.number().int(),
  requestedAt: z.date(),
  expiresAt: z.date(),
  resolvedAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertSchoolRemovalRequest = typeof SchoolRemovalRequest.$inferInsert
export type UpdateSchoolRemovalRequest = z.infer<typeof UpdateSchoolRemovalRequestSchema>
export type SelectSchoolRemovalRequest = typeof SchoolRemovalRequest.$inferSelect
